import { styled } from "@mui/material";

import { PATH } from "@/configs/router/routerConfig";
import { styleSheet } from "@/configs/styles";
import { Breadcrumbs, Menu } from "@/contexts/SidebarContext";
import { useSidebarMenus } from "@/hooks/useSidebarMenus";
import { useTranslation } from "next-i18next";
import NextLink from "next/link";
import { useRouter } from "next/router";

const BreadcrumbsStyled = styled("div")(
  ({ theme }) => `
    width: 100%;
    height: 70px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    .breadcrumb {
      width: 100%;
      height: 50%;
      display: flex;
      align-items: center;
      color: ${theme.palette.text.primary};
    }
    .breadcrumb a {
      text-decoration: none;
      color: ${theme.palette.text.secondary};
      font-weight: 500;
      font-size: 16px;
      transition: color 0.2s;
      vertical-align: middle;
    }
    .breadcrumb a:hover {
      color: ${theme.palette.text.primary};
    }
    .breadcrumb span.title {
      color: ${theme.palette.text.primary};
      font-weight: 700;
      font-size: 18px;
      text-transform: uppercase;
      letter-spacing: 1px;
      vertical-align: middle;
    }
    .breadcrumb span.separator {
      color: ${theme.palette.text.disabled};
      margin: 0 4px;
      font-size: 16px;
      font-weight: 400;
      vertical-align: middle;
    }
  `
);

type Props = {
  breadcrumbs: Breadcrumbs;
  isSandboxTenant?: boolean;
};
function BreadcrumbsSidebar(props: Props) {
  const { breadcrumbs } = props;
  const { t } = useTranslation();
  const menus = useSidebarMenus();
  const router = useRouter();
  const isSignupSetupRoute =
    router.pathname === PATH.SIGNUP_SETUP || String(router.asPath || "").startsWith(`${PATH.SIGNUP_SETUP}`);

  function findMenuPathByTitles(titles: string[], menus: Menu[]): string | null {
    let currentMenus = menus;
    let path = "";
    for (let i = 0; i < titles.length; i++) {
      const title = titles[i];
      const found = currentMenus.find((item) => item.title.toLowerCase() === title.toLowerCase());
      if (!found) return null;
      path = found.path;
      currentMenus = found.subMenu || [];
    }
    return path;
  }

  return (
    <BreadcrumbsStyled>
      <div className="breadcrumb">
        {breadcrumbs.breadcrumbs.map((crumb, i) => {
          const isLast = i === breadcrumbs.breadcrumbs.length - 1;
          const title = typeof crumb === "string" ? crumb : crumb.title;
          const path =
            typeof crumb === "object"
              ? crumb.path
              : findMenuPathByTitles(
                  breadcrumbs.breadcrumbs.slice(0, i + 1).map((c) => (typeof c === "string" ? c : c.title)),
                  menus
                );
          const isSignupCrumb = isSignupSetupRoute && title.toLowerCase() === "signup";
          const shouldRenderAsLink = !isLast && path && !isSignupCrumb;

          if (shouldRenderAsLink) {
            // Parent nodes: clickable links
            return (
              <span key={i}>
                <NextLink
                  href={path}
                  style={{
                    ...styleSheet.textStyleBasic,
                    textTransform: "capitalize",
                    verticalAlign: "middle",
                    marginRight: "6px",
                    display: "inline"
                  }}
                >
                  {t(title)}
                </NextLink>
                <span style={{ marginRight: "6px" }}>/</span>
              </span>
            );
          }
          // Last node: visually distinct, not clickable
          return (
            <span
              key={i}
              className="title"
              style={{
                ...styleSheet.textStyleBold,
                textTransform: "uppercase",
                verticalAlign: "middle",
                marginRight: "6px"
              }}
            >
              {t(title)}
              {/* SANDBOX badge */}
              {props.isSandboxTenant && (
                <span
                  style={{
                    background: "#FF9800",
                    color: "#fff",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    marginLeft: "10px",
                    fontSize: "14px",
                    fontWeight: 700,
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    verticalAlign: "middle",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
                  }}
                >
                  SANDBOX
                </span>
              )}
            </span>
          );
        })}
      </div>
    </BreadcrumbsStyled>
  );
}

export default BreadcrumbsSidebar;
